import {
  ACTION,
  AUTH_METHODS,
  AVATAR,
  AppError,
  CORE_MESSAGES,
  DEFAULT_ORG_AUTH_POLICY,
  EMAIL_PROVIDERS,
  ERROR_CODES,
  ErrorFactories,
  IDENTITY_LIMITS,
  IDENTITY_MESSAGES,
  LOCALES,
  MAX_ID_LENGTH,
  MAX_LOCALE_LENGTH,
  MAX_MESSAGE_KEY_LENGTH,
  ORG_RESOURCE,
  ORG_ROLE,
  ORG_ROLE_LABEL,
  PASSWORD_MFA,
  PLATFORM_AUTH_POLICY,
  PLATFORM_ROLE,
  PLATFORM_ROLES,
  SSO_PROVIDERS,
  SSO_PROVIDER_IDS,
  VALIDATION_MESSAGES,
  baSsoProviderId,
  backupCodeSchema,
  callerSessionSchema,
  can,
  catalogSsoProviderId,
  createOrgUserSchema,
  createOrganizationFormSchema,
  createOrganizationSchema,
  createPlatformAdminSchema,
  createRegistry,
  declaredMessageKeys,
  defineMessages,
  editOrganizationSchema,
  emailDomain,
  emailSchema,
  emailTestSchema,
  emailTransportSchema,
  fieldIssueSchema,
  isAppError,
  isAvatarContentType,
  localeSchema,
  matchLocale,
  memberRolesFormSchema,
  messageRefSchema,
  mfaPasswordConfirmSchema,
  msg,
  orgRoleFormSchema,
  orgRolePermissionsSchema,
  orgRoleSchema,
  organizationAuthPolicySchema,
  organizationEmailConfigSchema,
  passkeyNameSchema,
  passkeySummarySchema,
  passwordSchema,
  requireOrganizationId,
  resolveCompliant,
  setPasswordFormSchema,
  signInFieldsSchema,
  signInFormSchema,
  ssoAvailableQuerySchema,
  ssoAvailableSchema,
  ssoDiscoveryOrigins,
  ssoIssuerFor,
  ssoOidcRedirectUri,
  ssoProviderFormSchema,
  ssoProviderSchema,
  tenantIdFromIssuer,
  toMessageRef,
  totpCodeSchema,
  updateLocaleSchema,
  updateMemberSchema,
  updateOrgModulesSchema,
  updateOrganizationSchema,
  updateProfileSchema
} from "./chunk-ONOJXEKY.js";

// src/domains/calendars/index.ts
import { z } from "zod";

// src/domains/calendars/messages.ts
var CALENDAR_MESSAGES = defineMessages("calendar", [
  "today",
  "previousMonth",
  "nextMonth",
  "newEvent",
  "editEvent",
  "starts",
  "ends",
  "endBeforeStart",
  "repeat",
  "repeatNone",
  "repeatWeekly",
  "repeatMonthly",
  "repeatCount",
  "cancelEvent",
  "restoreEvent",
  "deleteConfirm",
  "filters"
]);

// src/domains/calendars/index.ts
var CALENDAR_LIMITS = {
  MAX_OCCURRENCES: 52,
  MAX_RANGE_DAYS: 62,
  MAX_FILTER_LENGTH: 200
};
function defineCalendar(calendar) {
  return calendar;
}
var instant = z.iso.datetime({ offset: true });
var timesSchema = z.object({ startsAt: instant, endsAt: instant }).refine(({ startsAt, endsAt }) => Date.parse(endsAt) > Date.parse(startsAt), {
  message: CALENDAR_MESSAGES.endBeforeStart,
  path: ["endsAt"]
});
var createEventsSchema = (calendar) => z.object({ data: calendar.dataSchema, occurrences: z.array(timesSchema).min(1).max(CALENDAR_LIMITS.MAX_OCCURRENCES) });
var updateEventSchema = (calendar) => z.object({ data: calendar.dataSchema, times: timesSchema, cancelled: z.boolean() });
var eventRangeSchema = (calendar) => z.object({
  from: instant,
  to: instant,
  ...Object.fromEntries(
    calendar.filters.map((field2) => [field2, z.string().trim().max(CALENDAR_LIMITS.MAX_FILTER_LENGTH).optional()])
  )
}).refine(({ from, to }) => {
  const span = Date.parse(to) - Date.parse(from);
  return span > 0 && span <= CALENDAR_LIMITS.MAX_RANGE_DAYS * 864e5;
});
var calendarEventSchema = z.object({
  id: z.uuid(),
  seriesId: z.uuid().nullable(),
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }),
  /** The calendar's own fields, shaped by its `dataSchema`. */
  data: z.record(z.string(), z.unknown()).readonly(),
  cancelled: z.boolean()
}).readonly();

// src/domains/imports/index.ts
import { z as z4 } from "zod";

// src/kernel/pagination.ts
import { z as z2 } from "zod";
var PAGE_SIZES = [25, 50, 100, 200];
var DEFAULT_PAGE_SIZE = 50;
var MAX_PAGE_SIZE = PAGE_SIZES[PAGE_SIZES.length - 1] ?? DEFAULT_PAGE_SIZE;
var SORT_DIRECTIONS = ["asc", "desc"];
var pageQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE)
});
function listQuerySchema(sortable) {
  return pageQuerySchema.extend({ sort: z2.enum(sortable).optional(), dir: z2.enum(SORT_DIRECTIONS).default("asc") });
}
function offsetOf(query) {
  return (query.page - 1) * query.pageSize;
}
function pageOf(items, total, query) {
  return { items, total, page: query.page, pageSize: query.pageSize };
}

// src/kernel/validation.ts
import { z as z3 } from "zod";
var KEY_PATTERN = /^[a-z][a-zA-Z0-9]*\.[a-zA-Z0-9]+$/;
function keyForIssue(issue) {
  switch (issue.code) {
    case "invalid_type":
      return issue.input === void 0 || issue.input === null ? VALIDATION_MESSAGES.required : VALIDATION_MESSAGES.invalid;
    case "too_small":
      return VALIDATION_MESSAGES.tooShort;
    case "too_big":
      return VALIDATION_MESSAGES.tooLong;
    case "invalid_format":
      return "format" in issue && issue.format === "email" ? VALIDATION_MESSAGES.invalidEmail : VALIDATION_MESSAGES.invalid;
    default:
      return VALIDATION_MESSAGES.invalid;
  }
}
function installValidationMessages() {
  z3.config({
    customError: (issue) => keyForIssue(issue)
  });
}
function paramsOf(params) {
  if (typeof params !== "object" || params === null) return {};
  return Object.fromEntries(
    Object.entries(params).filter(
      (entry) => ["string", "number", "boolean"].includes(typeof entry[1])
    )
  );
}
function issueKey(message) {
  return KEY_PATTERN.test(message) ? message : VALIDATION_MESSAGES.invalid;
}
function toFieldIssues(error) {
  return error.issues.map((issue) => {
    const params = paramsOf(issue.params);
    return {
      field: issue.path.map(String).join("."),
      message: Object.keys(params).length > 0 ? msg(issueKey(issue.message), params) : msg(issueKey(issue.message))
    };
  });
}
function toMessageRefs(error) {
  return toFieldIssues(error).map(({ field: field2, message }) => field2 ? msg(message.key, { ...message.params, field: field2 }) : message);
}

// src/domains/imports/messages.ts
var IMPORT_MESSAGES = defineMessages("imports", [
  "chooseFile",
  "dropPrompt",
  "dropHint",
  "reading",
  "staging",
  "unreadableFile",
  "emptyFile",
  "tooManyRows",
  "tooManyColumns",
  "mappingTitle",
  "notInFile",
  "unnamedColumn",
  "rowsEmpty",
  "rowsTitle",
  "validLabel",
  "invalidLabel",
  "fieldNotMapped"
]);

// src/domains/imports/index.ts
var IMPORT_LIMITS = {
  /** Rows per import (a count). */
  MAX_ROWS: 1e5,
  /** Highest sheet row a row can come from — Excel's own worksheet limit (row numbers skip blank rows). */
  MAX_ROW_NUMBER: 1048576,
  MAX_COLUMNS: 200,
  ROWS_PER_REQUEST: 500,
  /**
   * Cap for HTTP peer-index payloads. Above this, the editor skips the client
   * unique index (server still enforces uniqueness on save / finalize).
   * Shipping 100k peers over Traefik HTTP/2 hangs the API pool and surfaces as
   * `ERR_HTTP2_PROTOCOL_ERROR` after the entrypoint readTimeout.
   */
  PEER_INDEX_MAX_ROWS: 5e3,
  MAX_HEADER_LENGTH: 200,
  MAX_CELL_LENGTH: 2e3,
  MAX_FILENAME_LENGTH: 255,
  MAX_CONTENT_TYPE_LENGTH: 255,
  /** Ceiling on an archived original spreadsheet. */
  MAX_FILE_BYTES: 250 * 1024 * 1024,
  PRESIGN_EXPIRY_SECONDS: 15 * 60
};
function defineImporter(importer) {
  return importer;
}
var cellSchema = z4.union([z4.string().max(IMPORT_LIMITS.MAX_CELL_LENGTH), z4.number(), z4.null()]);
var cellsSchema = z4.record(z4.string().max(IMPORT_LIMITS.MAX_HEADER_LENGTH), cellSchema);
var columnMappingSchema = z4.record(z4.string(), z4.string().nullable());
var normalizeHeader = (header) => header.toLowerCase().replace(/[^a-z0-9]/g, "");
function autoMap(importer, columns) {
  return Object.fromEntries(
    Object.entries(importer.fields).map(([field2, { aliases }]) => [
      field2,
      columns.find((column) => aliases.includes(normalizeHeader(column))) ?? null
    ])
  );
}
function isValidMapping(importer, columns, mapping) {
  return Object.entries(mapping).every(([field2, column]) => field2 in importer.fields && (column === null || columns.includes(column)));
}
function uniqueFields(importer) {
  return Object.entries(importer.fields).filter(([, field2]) => field2.unique === true).map(([key]) => key);
}
function normalizeUniqueValue(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length === 0 ? null : text.toLowerCase();
}
var uniquePeerIndexSchema = z4.object({
  rows: z4.array(
    z4.object({
      id: z4.string().min(1),
      values: z4.record(z4.string(), z4.string())
    })
  ).readonly()
}).readonly();
function uniquePeerIndex(rows) {
  return uniquePeerIndexSchema.parse({ rows });
}
function buildUniqueIndex(rows, fields) {
  const byField = /* @__PURE__ */ new Map();
  for (const field2 of fields) {
    const groups = /* @__PURE__ */ new Map();
    for (const row of rows) {
      const normalized = normalizeUniqueValue(row.values[field2]);
      if (normalized == null) continue;
      const ids = groups.get(normalized);
      if (ids) ids.push(row.id);
      else groups.set(normalized, [row.id]);
    }
    byField.set(field2, groups);
  }
  return byField;
}
function uniqueIssuesFor(index, rowId, values, fields) {
  const out = [];
  for (const field2 of fields) {
    const normalized = normalizeUniqueValue(values[field2]);
    if (normalized == null) continue;
    const ids = index.get(field2)?.get(normalized);
    if (ids?.some((id) => id !== rowId)) out.push(msg(CORE_MESSAGES.alreadyExists, { field: field2 }));
  }
  return out;
}
function dupesAgainst(fields, rowId, values, lookup) {
  const index = "index" in lookup ? lookup.index : buildUniqueIndex([...lookup.peers.filter((peer) => peer.id !== rowId), { id: rowId, values }], fields);
  return uniqueIssuesFor(index, rowId, values, fields);
}
function uniqueValuesFromCells(importer, mapping, cells) {
  const values = {};
  for (const field2 of uniqueFields(importer)) {
    const column = mapping[field2];
    const raw = column ? cells[column] : void 0;
    values[field2] = raw == null ? "" : String(raw);
  }
  return values;
}
function uniqueValuesFromText(importer, text) {
  const values = {};
  for (const field2 of uniqueFields(importer)) {
    values[field2] = text[field2] ?? "";
  }
  return values;
}
function rowIssues(importer, text, rowId, lookup = { peers: [] }) {
  const schemaResult = importer.rowSchema.safeParse(text);
  const schemaIssues = schemaResult.success ? [] : toMessageRefs(schemaResult.error);
  const fields = uniqueFields(importer);
  if (fields.length === 0) return schemaIssues;
  return [...schemaIssues, ...dupesAgainst(fields, rowId, uniqueValuesFromText(importer, text), lookup)];
}
function validateRow(importer, mapping, cells, rowId = "", lookup) {
  const mapped = Object.fromEntries(
    Object.keys(importer.fields).map((field2) => {
      const column = mapping[field2];
      return [field2, column ? cells[column] : void 0];
    })
  );
  const result = importer.rowSchema.safeParse(mapped);
  const schemaIssues = result.success ? [] : toMessageRefs(result.error);
  const fields = uniqueFields(importer);
  let dupes = [];
  if (fields.length > 0 && rowId && lookup) {
    const text = Object.fromEntries(Object.entries(mapped).map(([field2, value]) => [field2, value == null ? "" : String(value)]));
    dupes = dupesAgainst(fields, rowId, uniqueValuesFromText(importer, text), lookup);
  }
  const issues = [...schemaIssues, ...dupes];
  if (issues.length > 0) return { success: false, issues };
  if (!result.success) return { success: false, issues: schemaIssues };
  return { success: true, data: result.data, issues: [] };
}
var createImportSchema = z4.object({
  filename: z4.string().min(1).max(IMPORT_LIMITS.MAX_FILENAME_LENGTH),
  columns: z4.array(z4.string().min(1).max(IMPORT_LIMITS.MAX_HEADER_LENGTH)).min(1).max(IMPORT_LIMITS.MAX_COLUMNS).refine((columns) => new Set(columns).size === columns.length)
});
var stageRowsSchema = z4.object({
  rows: z4.array(z4.object({ rowNumber: z4.number().int().min(1).max(IMPORT_LIMITS.MAX_ROW_NUMBER), cells: cellsSchema })).min(1).max(IMPORT_LIMITS.ROWS_PER_REQUEST),
  /** When true, run full validity sync after this batch (last batch of a stage). */
  finalize: z4.boolean().optional()
});
var IMPORT_SORTS = ["filename", "rowCount", "updatedAt"];
var importsQuerySchema = listQuerySchema(IMPORT_SORTS);
var IMPORT_ROW_VALIDITY = ["valid", "invalid"];
function importRowSorts(importer) {
  return ["rowNumber", ...Object.keys(importer.fields)];
}
function importRowsQuerySchema(importer) {
  return listQuerySchema(importRowSorts(importer)).extend({
    validity: z4.enum(IMPORT_ROW_VALIDITY).optional()
  });
}
var updateImportSchema = z4.object({ mapping: columnMappingSchema });
var updateImportRowSchema = z4.object({ cells: cellsSchema });
var archiveImportSchema = z4.object({
  filename: z4.string().min(1).max(IMPORT_LIMITS.MAX_FILENAME_LENGTH),
  contentType: z4.string().min(1).max(IMPORT_LIMITS.MAX_CONTENT_TYPE_LENGTH),
  sizeBytes: z4.number().int().positive().max(IMPORT_LIMITS.MAX_FILE_BYTES)
});
var importSummarySchema = z4.object({
  id: z4.uuid(),
  filename: z4.string(),
  columns: z4.array(z4.string()).readonly(),
  mapping: columnMappingSchema,
  objectKey: z4.string().nullable(),
  rowCount: z4.number().int(),
  /** Derived counts from `import_rows.valid` (not denormalised on the parent). */
  validCount: z4.number().int(),
  invalidCount: z4.number().int(),
  createdAt: z4.iso.datetime(),
  updatedAt: z4.iso.datetime()
}).readonly();
var importRowSchema = z4.object({ rowNumber: z4.number().int(), cells: cellsSchema }).readonly();
var presignedImportArchiveSchema = z4.object({ objectKey: z4.string(), url: z4.url(), headers: z4.record(z4.string(), z4.string()).readonly() }).readonly();

// src/kernel/i18n.ts
var PLACEHOLDER = /\{(\w+)\}/g;
function interpolate(template, params) {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (match, name) => {
    const value = params[name];
    return value === void 0 ? match : String(value);
  });
}
function createTranslator(locale, catalogue) {
  return (ref) => {
    const template = catalogue[ref.key];
    if (template === void 0) {
      console.warn(`[i18n] Missing translation for "${ref.key}" in locale "${locale}"`);
      return ref.key;
    }
    return interpolate(template, ref.params);
  };
}

// src/kernel/fields.ts
import { z as z5 } from "zod";
var CURRENCIES = ["EUR", "USD", "GBP"];
var CURRENCY_CODE_LENGTH = 3;
var AMOUNT_SCALE = 2;
var HUNDREDTHS = 100;
function unitsOf(hundredths) {
  return hundredths / HUNDREDTHS;
}
var fieldRegistry = z5.registry();
function fieldMeta(schema) {
  return fieldRegistry.get(schema);
}
function readField(schema, raw) {
  const result = z5.safeParse(schema, raw);
  return result.success ? { rejected: false, value: result.data } : { rejected: true };
}
function fieldText(schema, value) {
  if (value === null || value === void 0) return "";
  const input = fieldMeta(schema)?.input;
  if (input?.kind === "number" && typeof value === "number") {
    const magnitude = Math.abs(value);
    const whole = `${value < 0 ? "-" : ""}${Math.trunc(magnitude / HUNDREDTHS)}`;
    const fraction = String(magnitude % HUNDREDTHS).padStart(AMOUNT_SCALE, "0").slice(0, input.scale);
    return fraction ? `${whole}.${fraction}` : whole;
  }
  return String(value);
}
var cell = z5.union([z5.string(), z5.number(), z5.date(), z5.null(), z5.undefined()]).transform((value) => {
  if (value === null || value === void 0) return void 0;
  if (value instanceof Date) return isoDate(value);
  const text = String(value).trim();
  return text === "" ? void 0 : text;
});
function field(input, parse) {
  return cell.transform((text, ctx) => text === void 0 ? void 0 : parse(text, ctx)).optional().register(fieldRegistry, { required: false, input });
}
function required(schema) {
  const next = schema.transform((value, ctx) => {
    if (value !== void 0) return value;
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.required });
    return z5.NEVER;
  });
  const meta = fieldMeta(schema);
  return meta ? next.register(fieldRegistry, { ...meta, required: true }) : next;
}
function textField({ max }) {
  return field({ kind: "text", maxLength: max }, (text, ctx) => {
    if (text.length <= max) return text;
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.maxLength, params: { max } });
    return z5.NEVER;
  });
}
var PLAIN = /^([+-]?)(\d+)(?:[.,](\d+))?$/;
var GROUPED = /^([+-]?)(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?$/;
function parseAmount(text) {
  const compact = text.replace(/\s/g, "");
  const [, sign, whole, fraction = ""] = GROUPED.exec(compact) ?? PLAIN.exec(compact) ?? [];
  if (whole === void 0) return void 0;
  const magnitude = Number(whole.replaceAll(",", "")) * HUNDREDTHS + Number(fraction.slice(0, AMOUNT_SCALE).padEnd(AMOUNT_SCALE, "0"));
  return { hundredths: sign === "-" ? -magnitude : magnitude, scale: fraction.length };
}
function checkedNumber(text, ctx, scale, sign) {
  const parsed = parseAmount(text);
  if (!parsed || !Number.isSafeInteger(parsed.hundredths)) {
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.notANumber });
    return void 0;
  }
  if (parsed.scale > scale) {
    const message = scale === 0 ? VALIDATION_MESSAGES.wholeNumber : VALIDATION_MESSAGES.maxDecimals;
    ctx.addIssue({ code: "custom", message, params: { scale } });
    return void 0;
  }
  if (sign === "positive" && parsed.hundredths <= 0) {
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.positive });
    return void 0;
  }
  if (sign === "nonNegative" && parsed.hundredths < 0) {
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.notNegative });
    return void 0;
  }
  return parsed.hundredths;
}
function decimalField({ scale, sign }) {
  return field({ kind: "number", scale, signed: sign === "any" }, (text, ctx) => checkedNumber(text, ctx, scale, sign) ?? z5.NEVER);
}
function integerField({ sign }) {
  return field({ kind: "integer", signed: sign === "any" }, (text, ctx) => {
    const hundredths = checkedNumber(text, ctx, 0, sign);
    return hundredths === void 0 ? z5.NEVER : hundredths / HUNDREDTHS;
  });
}
function priceField() {
  return decimalField({ scale: AMOUNT_SCALE, sign: "nonNegative" });
}
function quantityField() {
  return integerField({ sign: "positive" });
}
function choiceField(options) {
  return field({ kind: "choice", options }, (text, ctx) => {
    const match = options.find((option) => option.toLowerCase() === text.toLowerCase());
    if (match) return match;
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.notAnOption });
    return z5.NEVER;
  });
}
function currencyField() {
  return choiceField(CURRENCIES);
}
var ISO_DAY = z5.iso.date();
var ISO_INSTANT = z5.iso.datetime({ offset: true, local: true });
function isoDate(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function dateField() {
  return field({ kind: "date" }, (text, ctx) => {
    if (ISO_DAY.safeParse(text).success) return text;
    if (ISO_INSTANT.safeParse(text).success) return text.slice(0, 10);
    ctx.addIssue({ code: "custom", message: VALIDATION_MESSAGES.invalidDate });
    return z5.NEVER;
  });
}

// src/kernel/result.ts
import { z as z6 } from "zod";
var apiErrorBodySchema = z6.object({ code: z6.string(), message: messageRefSchema, issues: z6.array(fieldIssueSchema).readonly().optional() }).readonly();
export {
  ACTION,
  AUTH_METHODS,
  AVATAR,
  AppError,
  CALENDAR_LIMITS,
  CALENDAR_MESSAGES,
  CORE_MESSAGES,
  CURRENCY_CODE_LENGTH,
  DEFAULT_ORG_AUTH_POLICY,
  DEFAULT_PAGE_SIZE,
  EMAIL_PROVIDERS,
  ERROR_CODES,
  ErrorFactories,
  IDENTITY_LIMITS,
  IDENTITY_MESSAGES,
  IMPORT_LIMITS,
  IMPORT_MESSAGES,
  IMPORT_SORTS,
  LOCALES,
  MAX_ID_LENGTH,
  MAX_LOCALE_LENGTH,
  MAX_MESSAGE_KEY_LENGTH,
  ORG_RESOURCE,
  ORG_ROLE,
  ORG_ROLE_LABEL,
  PAGE_SIZES,
  PASSWORD_MFA,
  PLATFORM_AUTH_POLICY,
  PLATFORM_ROLE,
  PLATFORM_ROLES,
  SSO_PROVIDERS,
  SSO_PROVIDER_IDS,
  VALIDATION_MESSAGES,
  apiErrorBodySchema,
  archiveImportSchema,
  autoMap,
  baSsoProviderId,
  backupCodeSchema,
  buildUniqueIndex,
  callerSessionSchema,
  can,
  catalogSsoProviderId,
  choiceField,
  createEventsSchema,
  createImportSchema,
  createOrgUserSchema,
  createOrganizationFormSchema,
  createOrganizationSchema,
  createPlatformAdminSchema,
  createRegistry,
  createTranslator,
  currencyField,
  dateField,
  decimalField,
  declaredMessageKeys,
  defineCalendar,
  defineImporter,
  defineMessages,
  editOrganizationSchema,
  emailDomain,
  emailSchema,
  emailTestSchema,
  emailTransportSchema,
  eventRangeSchema,
  fieldMeta,
  fieldText,
  importRowSorts,
  importRowsQuerySchema,
  importsQuerySchema,
  installValidationMessages,
  integerField,
  isAppError,
  isAvatarContentType,
  isValidMapping,
  listQuerySchema,
  localeSchema,
  matchLocale,
  memberRolesFormSchema,
  messageRefSchema,
  mfaPasswordConfirmSchema,
  msg,
  offsetOf,
  orgRoleFormSchema,
  orgRolePermissionsSchema,
  orgRoleSchema,
  organizationAuthPolicySchema,
  organizationEmailConfigSchema,
  pageOf,
  passkeyNameSchema,
  passkeySummarySchema,
  passwordSchema,
  priceField,
  quantityField,
  readField,
  requireOrganizationId,
  required,
  resolveCompliant,
  rowIssues,
  setPasswordFormSchema,
  signInFieldsSchema,
  signInFormSchema,
  ssoAvailableQuerySchema,
  ssoAvailableSchema,
  ssoDiscoveryOrigins,
  ssoIssuerFor,
  ssoOidcRedirectUri,
  ssoProviderFormSchema,
  ssoProviderSchema,
  stageRowsSchema,
  tenantIdFromIssuer,
  textField,
  toFieldIssues,
  toMessageRef,
  totpCodeSchema,
  uniqueFields,
  uniquePeerIndex,
  uniqueValuesFromCells,
  uniqueValuesFromText,
  unitsOf,
  updateEventSchema,
  updateImportRowSchema,
  updateImportSchema,
  updateLocaleSchema,
  updateMemberSchema,
  updateOrgModulesSchema,
  updateOrganizationSchema,
  updateProfileSchema,
  validateRow
};
//# sourceMappingURL=index.js.map